import type {
  AccountDetail,
  AccountMinCash,
  AccountSettingOutput,
  UpdateAccountSettingsRequest,
} from '@/types/api/accounts'
import type { MinimumCashMode, MinimumCashValue } from './minimum-cash-types'

import {
  MinCashMethod,
  validateMinCashAmount,
} from '@/portfolio/shared/account-sections/components/min-cash-validation'
import {
  ACCOUNT_SETTING_KEY_MIN_CASH_DOLLAR,
  ACCOUNT_SETTING_KEY_MIN_CASH_PERCENT,
  ACCOUNT_SETTING_KEY_MIN_CASH_SETTING,
  MIN_CASH_SETTING_VALUE_DOLLAR,
  MIN_CASH_SETTING_VALUE_PERCENT,
} from '@/types/api/accounts'
import {
  formatMoney,
  formatPercentage,
  PLACEHOLDER,
} from '@/utils/presentation/formatting'

const MIN_CASH_CLEAR_DELETE_KEYS = [
  ACCOUNT_SETTING_KEY_MIN_CASH_DOLLAR,
  ACCOUNT_SETTING_KEY_MIN_CASH_PERCENT,
  ACCOUNT_SETTING_KEY_MIN_CASH_SETTING,
] as const

export function getMinimumCashKey(value: MinimumCashValue | null): string {
  if (value == null) {
    return ''
  }
  return `${value.mode}:${value.value}`
}

function readMinCashSetting(value: unknown): MinimumCashMode | null {
  if (value === MIN_CASH_SETTING_VALUE_DOLLAR) {
    return 'usd'
  }
  if (value === MIN_CASH_SETTING_VALUE_PERCENT) {
    return 'percent'
  }
  return null
}

function settingValueByKey(
  settings: ReadonlyArray<{ setting_key: string; setting_value: string }>,
  key: string,
): string | undefined {
  return settings.find((entry) => entry.setting_key === key)?.setting_value
}

function deriveMinimumCashFromMinCashFields({
  setting,
  dollar,
  percentFraction,
}: {
  setting: unknown
  dollar: unknown
  percentFraction: unknown
}): MinimumCashValue | null {
  const mode = readMinCashSetting(setting)
  const dollarAmount = readNumber(dollar)
  const percentAmount = readNumber(percentFraction)

  if (mode === 'percent' && percentAmount != null && percentAmount > 0) {
    return { mode: 'percent', value: percentAmount }
  }

  if (mode === 'usd' && dollarAmount != null && dollarAmount > 0) {
    return { mode: 'usd', value: dollarAmount }
  }

  return null
}

/** Maps account settings API output into the minimum-cash editor value. */
export function deriveMinimumCashFromSettings(
  settings: ReadonlyArray<{ setting_key: string; setting_value: string }>,
): MinimumCashValue | null {
  return deriveMinimumCashFromMinCashFields({
    setting: settingValueByKey(settings, ACCOUNT_SETTING_KEY_MIN_CASH_SETTING),
    dollar: settingValueByKey(settings, ACCOUNT_SETTING_KEY_MIN_CASH_DOLLAR),
    percentFraction: settingValueByKey(
      settings,
      ACCOUNT_SETTING_KEY_MIN_CASH_PERCENT,
    ),
  })
}

/** Maps GET /account/:id/ `min_cash` into the minimum-cash editor value. */
export function deriveMinimumCashFromMinCashObject(
  minCash: AccountMinCash | null | undefined,
): MinimumCashValue | null {
  if (!minCash) {
    return null
  }

  return deriveMinimumCashFromMinCashFields({
    setting: minCash.min_cash_setting,
    dollar: minCash.min_cash_dollar,
    percentFraction: minCash.min_cash_percent,
  })
}

/** Builds GET /account/:id/ `min_cash` from account settings (e.g. after PUT settings). */
export function minCashObjectFromSettings(
  settings: ReadonlyArray<AccountSettingOutput>,
): AccountMinCash | undefined {
  const value = deriveMinimumCashFromSettings(settings)
  if (value == null) {
    return undefined
  }

  if (value.mode === 'usd') {
    return {
      min_cash_setting: MIN_CASH_SETTING_VALUE_DOLLAR,
      min_cash_dollar: String(value.value),
    }
  }

  return {
    min_cash_setting: MIN_CASH_SETTING_VALUE_PERCENT,
    min_cash_percent: percentSettingValue(value.value),
  }
}

/** Reads minimum cash from GET /account/:id/ (min_cash, settings, allocation, or legacy balance). */
export function deriveMinimumCashFromAccount(
  account: AccountDetail | undefined,
): MinimumCashValue | null {
  if (!account) {
    return null
  }

  const fromMinCash = deriveMinimumCashFromMinCashObject(account.min_cash)
  if (fromMinCash != null) {
    return fromMinCash
  }

  const fromSettings = deriveMinimumCashFromSettings(account.settings ?? [])
  if (fromSettings != null) {
    return fromSettings
  }

  const allocation = account.cash_allocation
  if (allocation && 'min_allocation_weight' in allocation) {
    const weight = allocation.min_allocation_weight
    if (weight > 0) {
      return { mode: 'percent', value: weight }
    }
  }

  if (allocation && 'min_allocation_market_value' in allocation) {
    const marketValue = allocation.min_allocation_market_value
    if (marketValue > 0) {
      return { mode: 'usd', value: marketValue }
    }
  }

  const legacyBalance = readNumber(account.minimum_cash_balance)
  if (legacyBalance != null && legacyBalance > 0) {
    return { mode: 'usd', value: legacyBalance }
  }

  return null
}

// SORM emits min_cash_setting as '$' / '%' (see account_min_cash_settings.sql.tpl
// in horizon). The REST API uses 'dollar' / 'percent' — read with
// readMinCashSetting above. Don't confuse the two: this reader is for SORM rows.
//
// The percent scale differs too. REST serves the stored fraction (0.05); SORM
// scales it to 0-100 for its percentage formatter, so SORM rows read 5 and the
// two readers below divide while the REST ones do not.
function readSormMinCashSetting(value: unknown): MinimumCashMode | null {
  if (value === '$') {
    return 'usd'
  }
  if (value === '%') {
    return 'percent'
  }
  return null
}

/**
 * SORM Account min-cash fallback when account settings aren't on the GET payload.
 * Reads the three mode-aware fields from a SORM Account row.
 */
export function deriveMinimumCashFromSorm({
  setting,
  dollar,
  percentPoints,
}: {
  setting: unknown
  dollar: unknown
  percentPoints: unknown
}): MinimumCashValue | null {
  const mode = readSormMinCashSetting(setting)
  if (mode == null) {
    return null
  }
  const dollarAmount = readNumber(dollar)
  const percentAmount = readNumber(percentPoints)

  if (mode === 'percent' && percentAmount != null && percentAmount > 0) {
    return { mode: 'percent', value: percentAmount / 100 }
  }
  if (mode === 'usd' && dollarAmount != null && dollarAmount > 0) {
    return { mode: 'usd', value: dollarAmount }
  }
  return null
}

/**
 * Effective dollar minimum-cash floor from the SORM Account row, applying the
 * mode: '$' → the dollar floor; '%' → percent/100 × percentBasis; otherwise
 * null. Mirrors the Investable cash expression in account.yaml — pass the basis
 * from minCashPercentBasis so the two agree.
 */
export function effectiveMinCashUsdFromSorm({
  setting,
  dollar,
  percentPoints,
  percentBasis,
}: {
  setting: unknown
  dollar: unknown
  percentPoints: unknown
  percentBasis: number | null
}): number | null {
  const mode = readSormMinCashSetting(setting)
  if (mode === 'usd') {
    const dollarAmount = readNumber(dollar)
    return dollarAmount != null && dollarAmount > 0 ? dollarAmount : null
  }
  if (mode === 'percent') {
    const percentAmount = readNumber(percentPoints)
    if (
      percentAmount == null ||
      percentAmount <= 0 ||
      percentBasis == null ||
      percentBasis <= 0
    ) {
      return null
    }
    return (percentAmount / 100) * percentBasis
  }
  return null
}

function readNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === 'string' && value.length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

export function formatMinimumCashLabel(value: MinimumCashValue | null): string {
  if (value == null) {
    return PLACEHOLDER
  }
  if (value.mode === 'percent') {
    return formatPercentage(value.value * 100, { numDigits: 2 })
  }
  return formatMoney(value.value)
}

function formatPercentDraft(displayPercent: number): string {
  if (!Number.isFinite(displayPercent) || displayPercent <= 0) {
    return ''
  }
  const rounded = Math.round(displayPercent * 1_000_000) / 1_000_000
  return rounded.toFixed(6).replace(/\.?0+$/, '')
}

function formatUsdDraft(amount: number): string {
  if (!Number.isFinite(amount) || amount <= 0) {
    return ''
  }
  const rounded = Math.round(amount * 100) / 100
  return String(rounded)
}

export function draftFromMinimumCash(value: MinimumCashValue | null): string {
  if (value == null) {
    return ''
  }
  if (value.mode === 'percent') {
    return formatPercentDraft(value.value * 100)
  }
  return formatUsdDraft(value.value)
}

/** Converts a semantic minimum-cash value into the target mode. */
export function convertMinimumCashValueToMode({
  value,
  targetMode,
  percentBasis,
}: {
  value: MinimumCashValue | null
  targetMode: MinimumCashMode
  percentBasis: number | null
}): MinimumCashValue | null {
  if (value == null) {
    return null
  }
  if (value.mode === targetMode) {
    return value
  }

  if (percentBasis == null || percentBasis <= 0) {
    return null
  }

  if (targetMode === 'percent') {
    return { mode: 'percent', value: value.value / percentBasis }
  }

  const usdAmount = Math.round(value.value * percentBasis * 100) / 100
  return { mode: 'usd', value: usdAmount }
}

/** Resolves the semantic minimum-cash value being edited from draft and saved state. */
export function resolveMinimumCashEditingValue({
  mode,
  draft,
  initialValue,
}: {
  mode: MinimumCashMode
  draft: string
  initialValue: MinimumCashValue | null
}): MinimumCashValue | null {
  const parsed = parseDraftToMinimumCash({ mode, draft })
  if (parsed != null) {
    return parsed
  }

  if (initialValue == null || initialValue.mode !== mode) {
    return null
  }

  const initialDraft = draftFromMinimumCash(initialValue)
  if (draft === initialDraft) {
    return initialValue
  }

  return null
}

function draftForMinimumCashMode({
  value,
  mode,
  percentBasis,
}: {
  value: MinimumCashValue
  mode: MinimumCashMode
  percentBasis: number | null
}): string | null {
  const converted = convertMinimumCashValueToMode({
    value,
    targetMode: mode,
    percentBasis,
  })
  if (converted == null) {
    return null
  }
  return draftFromMinimumCash(converted)
}

/** Converts the in-progress draft when toggling between USD and percent modes. */
export function convertMinimumCashDraftOnModeChange({
  fromMode,
  toMode,
  draft,
  initialValue,
  percentBasis,
}: {
  fromMode: MinimumCashMode
  toMode: MinimumCashMode
  draft: string
  initialValue: MinimumCashValue | null
  percentBasis: number | null
}): string {
  if (fromMode === toMode) {
    return draft
  }

  const editingValue = resolveMinimumCashEditingValue({
    mode: fromMode,
    draft,
    initialValue,
  })
  if (editingValue == null) {
    return ''
  }

  const convertedDraft = draftForMinimumCashMode({
    value: editingValue,
    mode: toMode,
    percentBasis,
  })
  if (convertedDraft != null) {
    return convertedDraft
  }

  return draft
}

function minCashMethodForMode(mode: MinimumCashMode): MinCashMethod {
  return mode === 'percent'
    ? MinCashMethod.Percentage
    : MinCashMethod.CashAmount
}

export function parseDraftToMinimumCash({
  mode,
  draft,
}: {
  mode: MinimumCashMode
  draft: string
}): MinimumCashValue | null {
  const trimmed = draft.trim()
  if (trimmed === '') {
    return null
  }
  const parsed = Number.parseFloat(trimmed.replace(/[$,]/g, ''))
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null
  }
  if (mode === 'percent') {
    return { mode: 'percent', value: parsed / 100 }
  }
  return { mode: 'usd', value: parsed }
}

export function validateMinimumCashDraft({
  mode,
  draft,
}: {
  mode: MinimumCashMode
  draft: string
}): string | null {
  const trimmed = draft.trim()
  if (trimmed === '') {
    return null
  }
  return validateMinCashAmount(trimmed, minCashMethodForMode(mode))
}

export function formatMinimumCashUsdPreview({
  mode,
  draft,
  percentBasis,
}: {
  mode: MinimumCashMode
  draft: string
  percentBasis: number | null
}): string | null {
  if (mode !== 'percent' || percentBasis == null || percentBasis <= 0) {
    return null
  }
  const trimmed = draft.trim()
  if (trimmed === '') {
    return null
  }
  const parsed = Number.parseFloat(trimmed.replace(/[$,]/g, ''))
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null
  }
  return formatMoney((parsed / 100) * percentBasis)
}

// The setting stores a fraction in (0, 1], parsed at scale 6 by accounts and
// ClickHouse, so round to 6 decimal places rather than emitting a whole percent.
function percentSettingValue(decimalFraction: number): string {
  return String(Math.round(decimalFraction * 1_000_000) / 1_000_000)
}

export function minimumCashSettingsBody(
  value: MinimumCashValue | null,
): UpdateAccountSettingsRequest {
  if (value == null) {
    return {
      settings: [],
      delete_keys: [...MIN_CASH_CLEAR_DELETE_KEYS],
    }
  }

  if (value.mode === 'usd') {
    return {
      settings: [
        {
          setting_key: ACCOUNT_SETTING_KEY_MIN_CASH_SETTING,
          setting_value: MIN_CASH_SETTING_VALUE_DOLLAR,
        },
        {
          setting_key: ACCOUNT_SETTING_KEY_MIN_CASH_DOLLAR,
          setting_value: String(value.value),
        },
      ],
      delete_keys: [ACCOUNT_SETTING_KEY_MIN_CASH_PERCENT],
    }
  }

  return {
    settings: [
      {
        setting_key: ACCOUNT_SETTING_KEY_MIN_CASH_SETTING,
        setting_value: MIN_CASH_SETTING_VALUE_PERCENT,
      },
      {
        setting_key: ACCOUNT_SETTING_KEY_MIN_CASH_PERCENT,
        setting_value: percentSettingValue(value.value),
      },
    ],
    delete_keys: [ACCOUNT_SETTING_KEY_MIN_CASH_DOLLAR],
  }
}

export function minimumCashValuesEqual(
  a: MinimumCashValue | null,
  b: MinimumCashValue | null,
): boolean {
  if (a == null && b == null) {
    return true
  }
  if (a == null || b == null) {
    return false
  }
  return a.mode === b.mode && a.value === b.value
}
